import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { HotPageSkeleton } from 'pages/tours/hot-tours-page/components/hot-pape-skeleton'
import {
  HotToursTabs,
  HotToursTabsList,
} from 'pages/tours/hot-tours-page/model.ts'
import { filterHotTours } from 'pages/tours/main-page/components/hot-tours/lib.ts'

import { prepareHotTour } from 'entities/tour/libs/prepareHotTour.ts'
import hotTourStore from 'entities/tour/models/hot-tours/hot-tour.ts'
import searchTour from 'entities/tour/models/search-tour/search-tour.ts'
import { HotTourCardMini } from 'entities/tour/ui/hot-tour/components/hot-tour-card-mini'

import { HotTour } from 'shared/api/tours/search'
import { ButtonCheckBox, ISelectHandler } from 'shared/ui/button-check-box'
import { HeaderGoBack } from 'shared/ui/header-go-back'

import s from './styles.module.scss'

const HotToursPage = observer(() => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const tabFromUrl = searchParams.get('tab') || HotToursTabs.bishkek.key
  const [active, setActive] = useState<string>(tabFromUrl)

  const hotTours = hotTourStore.getHotTourList()
  const isLoading = hotTourStore.isLoading

  const getTours = () => {
    hotTourStore.removeTourDetail()

    if (!hotTours?.length) void hotTourStore.requestHotToursList()
  }

  const onTabClick = (tab: ISelectHandler) => {
    setActive(tab.name)
    setSearchParams({ tab: tab.name }, { replace: true })
  }

  const onTourClick = (tour?: HotTour) => {
    const preparedTour = prepareHotTour(tour)

    searchTour.setDataForSearchTour(preparedTour)

    navigate(RoutingPaths.tours.hotTourDetailPage.path(tour?.tourid))
  }

  useEffect(() => {
    void getTours()
  }, [])

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    if (tabFromUrl && tabFromUrl !== active) {
      setActive(tabFromUrl)
    }
  }, [searchParams])

  const filteredTours = useMemo(
    () => filterHotTours(hotTours, active),
    [hotTours],
  )

  return (
    <div className={s.container}>
      <HeaderGoBack
        buttonWhite
        variant="grey"
        title={t('tours_block.hot_tours')}
      />

      <div className={s.box}>
        <ButtonCheckBox
          onChange={onTabClick}
          buttons={HotToursTabsList}
          active={active}
        />

        {isLoading ? (
          <HotPageSkeleton />
        ) : (
          <div className={s.toursList}>
            {filteredTours?.map((tour) => (
              <HotTourCardMini
                tour={tour}
                key={tour.tourid}
                onClick={onTourClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
})

export default HotToursPage
